import React, { Component, Suspense, useState, useCallback, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, PerspectiveCamera, Stars } from '@react-three/drei'
import { OrbField } from './OrbField'
import { Modal } from './Modal'
import { LoadingScreen } from './LoadingScreen'
import { modalAnimation } from './animations'
import styles from './styles.module.css'
import { KnowledgeKey } from './KnowledgeKey'
import Flashcard from '../components/Flashcard/Flashcard'

class WebGLErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    console.warn('Falling back from 3D Knowledge Base:', error)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}

const canCreateWebGLContext = () => {
  if (typeof window === 'undefined') {
    return false
  }

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('webgl2') || canvas.getContext('webgl')

  if (!context) {
    return false
  }

  const loseContext = context.getExtension('WEBGL_lose_context')
  if (loseContext) {
    loseContext.loseContext()
  }

  return true
}

const KnowledgeFallback = () => (
  <div className={styles.webglFallback}>
    <h3>Knowledge Base</h3>
    <p>
      The interactive 3D view is unavailable in this browser session, so here is
      the simplified knowledge view instead.
    </p>
    <Flashcard />
  </div>
)

export const KnowledgeBase = () => {
  const [selectedOrb, setSelectedOrb] = useState(null)
  const [showEasterEgg, setShowEasterEgg] = useState(false)
  const [hasWebGL] = useState(() => canCreateWebGLContext())

  const handleDoubleClick = useCallback(() => {
    setShowEasterEgg(true)
  }, [])

  // Generate random star positions
  const starProps = useMemo(() => ({
    radius: 100, // Larger radius to encompass the scene
    depth: 50, // More depth for better parallax
    count: 5000, // More stars
    factor: 4, // Star size distribution
    saturation: 0, // Whiter stars
    fade: true, // Fade with distance
    speed: 2, // Rotation speed
  }), [])

  if (!hasWebGL) {
    return <KnowledgeFallback />
  }

  return (
    <div className={styles.container} onDoubleClick={handleDoubleClick}>
      <KnowledgeKey />
      
      <div className={styles.starLayer1}></div>
      <div className={styles.starLayer2}></div>
      <div className={styles.starLayer3}></div>
      <div className={styles.twinkle}></div>
      
      <div className={styles.gradientOverlay}></div>
      
      <WebGLErrorBoundary fallback={<KnowledgeFallback />}>
        <Suspense fallback={<LoadingScreen />}>
          <Canvas
            className={styles.canvas}
            dpr={[1, 1.5]}
            gl={{ antialias: true, powerPreference: 'high-performance' }}
          >
            <PerspectiveCamera
              makeDefault
              position={[0, 0, 35]}
              fov={60}
              near={0.1}
              far={1000}
            />
            
            <OrbitControls 
              enablePan={true}
              minDistance={15}
              maxDistance={40}
              enableDamping
              dampingFactor={0.05}
              rotateSpeed={0.5}
            />

            {/* Add 3D stars that rotate with camera */}
            <Stars {...starProps} />
            
            {/* Existing lighting */}
            <ambientLight intensity={0.4} />
            <pointLight position={[10, 10, 10]} intensity={0.8} />
            <pointLight position={[-10, -10, -10]} intensity={0.5} />
            <spotLight
              position={[0, 10, 0]}
              angle={0.5}
              penumbra={1}
              intensity={0.8}
            />

            {/* Main 3D content */}
            <OrbField onOrbClick={setSelectedOrb} />

            {/* Adjusted fog for less intensity */}
            <fog attach="fog" args={['#0f0f1a', 30, 55]} />
          </Canvas>
        </Suspense>
      </WebGLErrorBoundary>

      {/* Instructions */}
      <div className={styles.instructions}>
        Drag to rotate • Scroll to zoom • Click orb for details
      </div>

      {/* Modals */}
      {selectedOrb && (
        <Modal
          isOpen={!!selectedOrb}
          onClose={() => setSelectedOrb(null)}
          content={selectedOrb}
          animation={modalAnimation}
        />
      )}

      {showEasterEgg && (
        <Modal
          isOpen={showEasterEgg}
          onClose={() => setShowEasterEgg(false)}
          content={{
            title: "Knowledge Base Visualization",
            description: "This 3D knowledge map represents interconnected technical skills and tools. The color-coded orbs show different categories, while the connecting lines represent relationships and common workflows between technologies. The floating animation and glow effects create an immersive learning environment inspired by sci-fi holographic interfaces.",
            additionalContent: "Double-click anywhere to close this message and explore the knowledge base!"
          }}
          animation={modalAnimation}
        />
      )}
    </div>
  )
}